import React, { FunctionComponent, ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import env from 'env';
import { useCurrentBilikPerson } from 'hooks/use-current-bilik-person/use-current-bilik-person';

interface DataLayerObject {
  event: string;
  [key: string]: any;
}

declare global {
  interface Window {
    dataLayer: DataLayerObject[];
  }
}

type AnalyticsProviderProps = {
  enabled: boolean;
  children: ReactNode;
};

export const AnalyticsProvider: FunctionComponent<AnalyticsProviderProps> = ({
  enabled,
  children,
}) => {
  const { currentBilikPerson } = useCurrentBilikPerson();
  const location = useLocation();

  useEffect(() => {
    if (enabled && env.GA_TRACKING_ID) {
      console.log('GA Tracking ID found, initializing ReactGA');
      ReactGA.initialize(env.GA_TRACKING_ID);
    }
  }, [enabled]);

  useEffect(() => {
    if (enabled && env.GA_TRACKING_ID) {
      ReactGA.send({
        hitType: 'pageview',
        page: location.pathname + location.search,
      });
    }
  }, [enabled, location]);

  useEffect(() => {
    const handleGlobalClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const element = target.closest<HTMLElement>('[data-ga-event]');

      if (element) {
        const { gaEvent, ...gaParams } = element.dataset;
        const eventName = gaEvent || 'generic_event';
        const userId = currentBilikPerson ? currentBilikPerson.id : 'anonymous';

        const gtmEvent = {
          event: eventName,
          user_id: userId,
          ...gaParams,
        };
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(gtmEvent);

        ReactGA.event(eventName, {
          user_id: userId,
          ...gaParams,
        });

        console.log('Événement GTM & GA envoyé :', gtmEvent);
      }
    };

    if (enabled) {
      document.addEventListener('click', handleGlobalClick);
    }

    return () => {
      if (enabled) {
        document.removeEventListener('click', handleGlobalClick);
      }
    };
  }, [enabled, currentBilikPerson]);

  return <>{children}</>;
};
